/**
 * 文件定位：
 * 1. 当前文件位于 cad-core/feature/rollback；
 * 2. 负责按退回控制棒状态同步用户基准面/轴/点显隐，并维护可见性快照；
 * 3. 不依赖 Viewer 或 Manager 具体类型，便于单测与 Viewer 门面注入。
 */

/**
 * 用户基准几何可见性读写端口。
 */
export interface FeatureRollbackDatumVisibilityPort {
    /**
     * 读取基准面当前可见性。
     *
     * @param planeId 基准面 ID（字符串）。
     * @returns 可见性；对象不存在时返回 `null`。
     */
    readonly readPlaneVisible: (planeId: string) => boolean | null;
    /**
     * 写入基准面可见性。
     *
     * @param planeId 基准面 ID（字符串）。
     * @param visible 目标可见性。
     */
    readonly writePlaneVisible: (planeId: string, visible: boolean) => void;
    /**
     * 读取基准轴当前可见性。
     *
     * @param axisId 基准轴 ID（字符串）。
     * @returns 可见性；对象不存在时返回 `null`。
     */
    readonly readAxisVisible: (axisId: string) => boolean | null;
    /**
     * 写入基准轴可见性。
     *
     * @param axisId 基准轴 ID（字符串）。
     * @param visible 目标可见性。
     */
    readonly writeAxisVisible: (axisId: string, visible: boolean) => void;
    /**
     * 读取基准点当前可见性。
     *
     * @param pointId 基准点 ID（字符串）。
     * @returns 可见性；对象不存在时返回 `null`。
     */
    readonly readPointVisible: (pointId: string) => boolean | null;
    /**
     * 写入基准点可见性。
     *
     * @param pointId 基准点 ID（字符串）。
     * @param visible 目标可见性。
     */
    readonly writePointVisible: (pointId: string, visible: boolean) => void;
}

/**
 * 退回控制棒基准显隐同步输入。
 */
export interface FeatureRollbackDatumVisibilitySyncInput extends FeatureRollbackDatumVisibilityPort {
    /** 控制棒下方的用户基准面 ID。 */
    readonly rolledBackPlaneIds?: readonly string[];
    /** 控制棒下方的用户基准轴 ID。 */
    readonly rolledBackAxisIds?: readonly string[];
    /** 控制棒下方的用户基准点 ID。 */
    readonly rolledBackPointIds?: readonly string[];
    /** 进入退回隐藏前的基准面可见性快照；由本函数原地维护。 */
    readonly planeVisibleSnapshot: Map<string, boolean>;
    /** 进入退回隐藏前的基准轴可见性快照；由本函数原地维护。 */
    readonly axisVisibleSnapshot: Map<string, boolean>;
    /** 进入退回隐藏前的基准点可见性快照；由本函数原地维护。 */
    readonly pointVisibleSnapshot: Map<string, boolean>;
}

/**
 * 同步一类基准几何的退回显隐。
 *
 * @param rolledBackIds 控制棒下方的对象 ID。
 * @param snapshot 进入退回前的可见性快照。
 * @param readVisible 读取当前可见性。
 * @param writeVisible 写入目标可见性。
 * @returns 是否发生可见性变更。
 */
function syncRolledBackVisibilityBucket(
    rolledBackIds: ReadonlySet<string>,
    snapshot: Map<string, boolean> | undefined,
    readVisible: (id: string) => boolean | null,
    writeVisible: (id: string, visible: boolean) => void,
): boolean {
    if (!(snapshot instanceof Map)) {
        return false;
    }

    let changed = false;

    rolledBackIds.forEach((objectId) => {
        if (!snapshot.has(objectId)) {
            snapshot.set(objectId, readVisible(objectId) ?? true);
        }
        if (readVisible(objectId) !== false) {
            writeVisible(objectId, false);
            changed = true;
        }
    });

    [...snapshot.entries()].forEach(([objectId, visible]) => {
        if (rolledBackIds.has(objectId)) {
            return;
        }
        if (readVisible(objectId) !== visible) {
            writeVisible(objectId, visible);
            changed = true;
        }
        snapshot.delete(objectId);
    });

    return changed;
}

/**
 * 按退回控制棒状态同步用户基准面/轴/点显隐。
 *
 * 设计说明：
 * 1. 用户基准不在 Worker feature history，只能在 Viewer 侧按 cursor 旁路收口；
 * 2. 首次进入退回隐藏时记录原可见性，离开退回后按快照恢复，避免覆盖用户主动隐藏；
 * 3. 选择层已按 `isVisible()` 过滤拾取目标，隐藏后自然不可再选为参考。
 *
 * @param input 同步输入。
 * @returns 是否发生可见性变更。
 */
export function syncFeatureRollbackDatumVisibility(
    input: FeatureRollbackDatumVisibilitySyncInput,
): boolean {
    const rolledBackPlaneIds = new Set(input.rolledBackPlaneIds ?? []);
    const rolledBackAxisIds = new Set(input.rolledBackAxisIds ?? []);
    const rolledBackPointIds = new Set(input.rolledBackPointIds ?? []);

    const planeChanged = syncRolledBackVisibilityBucket(
        rolledBackPlaneIds,
        input.planeVisibleSnapshot,
        input.readPlaneVisible,
        input.writePlaneVisible,
    );
    const axisChanged = syncRolledBackVisibilityBucket(
        rolledBackAxisIds,
        input.axisVisibleSnapshot,
        input.readAxisVisible,
        input.writeAxisVisible,
    );
    const pointChanged = syncRolledBackVisibilityBucket(
        rolledBackPointIds,
        input.pointVisibleSnapshot,
        input.readPointVisible,
        input.writePointVisible,
    );

    return planeChanged || axisChanged || pointChanged;
}
